import { createApp, type Ref } from 'vue'
import type SearchResultsMapClass from '~/utils/maps/SearchResultsMap'
import { useSearchParamsStore } from '~~/stores/searchParams'
import { useSearchResultsMapStore } from '~~/stores/searchResultsMap'
import SharedObjectCard from '~/components/page-sections/_shared/SharedObjectCard.vue'

const useSearchResultsMapMarkers = (
  searchResultsMap: Ref<SearchResultsMapClass | null>,
  props: { data: any; pending: boolean },
  injectedDataRef: Ref<any> | null = null
) => {
  const searchParamsStore = useSearchParamsStore()
  const mapStore = useSearchResultsMapStore()

  const stopWatchers: Array<() => void> = []

  function createPoiMap (results: any[]): Map<string, any[]> {
    const poiMap = new Map<string, any[]>()
    if (results.length === 0) {
      return poiMap
    }
    results.forEach((item) => {
      if (!poiMap.has(item.poi)) {
        poiMap.set(item.poi, [])
      }
      poiMap.get(item.poi)!.push(item)
    })
    return poiMap
  }

  function getDuplicatePoiResults (poiMap: Map<string, any[]>): any[] {
    if (poiMap.size === 0) {
      return []
    }
    return Array.from(poiMap.values()).filter(items => items.length > 1).flat()
  }

  function spreadOverlappingPointsRadially (duplicatePoiResults: any[], radius: number = 0.015): void {
    if (duplicatePoiResults.length === 0) {
      return
    }
    const angleSteps = duplicatePoiResults.map((_, index) => (2 * Math.PI * index) / duplicatePoiResults.length)
    const earthRadius = 6371
    duplicatePoiResults.forEach((item, index) => {
      const [lat, lng] = item.poi.split(',').map(Number)
      const angleStep = angleSteps[index]

      // This calculation is necessary because otherwise the POIs are not placed in a circle but in an ellipse
      const x = earthRadius * Math.cos(lat * Math.PI / 180) * Math.cos(lng * Math.PI / 180)
      const y = earthRadius * Math.cos(lat * Math.PI / 180) * Math.sin(lng * Math.PI / 180)
      const z = earthRadius * Math.sin(lat * Math.PI / 180)

      // Apply the offset in Cartesian coordinates
      const newX = x + Math.cos(angleStep) * radius
      const newY = y + Math.sin(angleStep) * radius

      // Convert back to latitude and longitude
      const newLat = Math.atan2(z, Math.sqrt(newX * newX + newY * newY)) * 180 / Math.PI
      const newLng = Math.atan2(newY, newX) * 180 / Math.PI

      item.poi = `${newLat},${newLng}`
    })
  }

  function getFormattedPrice (price: number): string {
    return `${Math.ceil(price).toLocaleString('de-DE')} €`
  }

  function getCompletePrice (item: any): string {
    if (item.vari_price_gesamtpreis) {
      return getFormattedPrice(item.vari_price_gesamtpreis)
    }
    
    if (item.gesamtpreis) {
      return getFormattedPrice(item.gesamtpreis)
    }

    if (item.min) {
      return `ab ${getFormattedPrice(item.min)}`
    }

    return '-'
  }

  function hasDiscount (item: any) {
    return !!usePrices().totalPriceDiscountPercentage(
      item.vari_price_gesamtpreis_original ?? item.gesamtpreis_original,
      item.vari_price_gesamtpreis ?? item.gesamtpreis
    )
  }

  let mapMarkerObserver: IntersectionObserver | null = null

  function disconnectMapMarkerObserver () {
    if (!mapMarkerObserver) {
      return
    }
    mapMarkerObserver.disconnect()
    mapMarkerObserver = null
  }

  function createMapMarkerObserver (markerElement: Element) {
    mapMarkerObserver = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting) {
        markerElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
      disconnectMapMarkerObserver()
    }, { threshold: 0 })

    mapMarkerObserver.observe(markerElement)
  }

  function scrollToMapMarker (newValue: any, oldValue: any) {
    if (oldValue) {
      disconnectMapMarkerObserver()
    }
    if (!newValue) {
      return
    }
    const markerElement = document.querySelector('.leaflet-custom-search-results-map-div-icon.hovered')
    if (!markerElement) {
      return
    }
    createMapMarkerObserver(markerElement)
  }

  const registerWatchers = () => {
    stopWatchers.push(watch(() => mapStore.hoveredObject, (newValue, oldValue) => {
      if (!searchResultsMap.value) {
        return
      }
      searchResultsMap.value.toggleMarkerClassList(newValue as unknown as number, 'hovered', true)
      searchResultsMap.value.toggleMarkerClassList(oldValue as unknown as number, 'hovered', false)
      scrollToMapMarker(newValue, oldValue)
    }))

    stopWatchers.push(watch(() => searchParamsStore.serp_highlight, (newValue, oldValue) => {
      if (newValue === undefined || newValue === 0 || !searchResultsMap.value) {
        return
      }
      searchResultsMap.value.toggleMarkerClassList(newValue as number, 'highlighted', true)
      searchResultsMap.value.toggleMarkerClassList(oldValue as number, 'highlighted', false)
      searchResultsMap.value.openPopup(newValue as number)
    }, { immediate: true }))
  }

  const updateMarkers = () => {
    if (!searchResultsMap.value) {
      return
    }

    // Use injected raw useFetch ref when available (more reactive than props)
    const data = injectedDataRef ? injectedDataRef.value?.data : props.data

    let i = 0
    data?.alternative_results?.strategies.forEach((strategy: any) => {
      strategy.plain.forEach((item: any) => {
        item.strategy = strategy.name
      })
    })

    const combinedResults = useSearchResultsHelper().getCombinedResults(
      data?.normal_results?.plain,
      data?.alternative_results?.strategies
    )

    const poiMap = createPoiMap(combinedResults)
    const duplicatePoiResults = getDuplicatePoiResults(poiMap)
    if (duplicatePoiResults.length > 0) {
      spreadOverlappingPointsRadially(duplicatePoiResults)
    }

    const currentSearchResultsMap = searchResultsMap.value
    for (const item of combinedResults) {
      if (mapStore.lastOpenObject !== item.nr) {
        const app = createApp(SharedObjectCard, {
          design: 'leafletmap',
          maskedLink: true,
          item,
          serpHighlighted: searchParamsStore.serp_highlight === item.nr,
          mapMarker: true,
          wishlistNone: true,
          alternativeObject: item.strategy === 'flexi_arrival' || item.strategy === 'radius' || item.strategy === 'flexi_nights'
        })
        const popupComponent = app.mount(document.createElement('div'))
        popupComponent.$el.classList.add('leaflet-custom-search-results-map-popup-component')
        currentSearchResultsMap.addMarker(
          i,
          item.poi,
          item.nr,
          getCompletePrice(item),
          popupComponent.$el,
          item.has_lm_boolean_df,
          hasDiscount(item)
        )
      }
      i++
    }
  }

  return {
    updateMarkers,
    registerWatchers,
    stopWatchers
  }
}

export default useSearchResultsMapMarkers
